import { sectionRegistry } from './sectionRegistry';
import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { bt } from '../i18n';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  PageRenderer,
  applyThemeVars,
  pageOpensOnMedia,
  splitChromeSections,
} from '@aquilixshop/ui-react';
import { serializeComposed, useBuilderStore } from '../state/builderStore';
import { canvasBuyBoxJourneys, useStoreIsInCartMode } from '../state/cartMode';
import { syncThemeFontLink } from './themeFontLink';
import { MemoSection } from './MemoSection';
import { useGenerationStore } from '../state/generation/generationStore';
import { SelectionFrame } from './SelectionFrame';
import { HoverFrame } from './HoverFrame';
import { DeviceFrame } from './DeviceFrame';
import { InsertSectionAffordance } from './InsertSectionAffordance';
import { usePickedProducts } from './usePickedProducts';
import { useCanvasMediaRecovery } from './useCanvasMediaRecovery';
import { useHostNode } from './useHostNode';
import { useCanvasReveals } from './useCanvasReveals';
import { useResolvedCollections } from './useResolvedCollections';
import { useCountdownPreview } from './useCountdownPreview';
import { CountdownAppChrome } from './CountdownAppChrome';
import { useResolvedMenus } from './useResolvedMenus';
import { buildAnnouncements } from './dnd/announcements';

/**
 * The editing canvas (build-plan §4.1, §9.1). Renders one `<MemoSection key={id}/>` per section id from
 * the store's `order[]` — the GRANULAR per-section path, NOT raw `PageRenderer`. Each `MemoSection`
 * subscribes to only its own store slice, so a settings edit re-renders exactly one section.
 *
 * The sections are the IDENTICAL `@aquilixshop/ui-react` registry components the storefront paints,
 * wrapped only in editor-unaware `EditableSection` chrome — same React tree, no iframe, no postMessage.
 *
 * M3.3 wraps the list in a dnd-kit `DndContext` + `SortableContext` keyed by `order[]` (stable section
 * ids, never indexes). A `PointerSensor` (~6px activation so a click isn't a drag) handles mouse; a
 * `KeyboardSensor` with `sortableKeyboardCoordinates` makes reorder fully keyboard-operable. On drop we
 * dispatch ONE `moveSection(id, toIndex)` (one undo entry). A label-only `DragOverlay` ghost avoids
 * janky full-DOM clones. The rail reorders the SAME `order[]` via its own context (one model, two views).
 */
export function BuilderCanvas() {
  const order = useBuilderStore((s) => s.order);
  // «À partir de zéro» (0059, founder 2026-08-20): during a takeover RUN the page assembles — only the
  // sections already built are rendered, in order, each entering with a short rise (CSS below). The
  // document itself is untouched; this is a lens on the canvas, dropped the moment the run ends.
  const takeoverBuilding = useGenerationStore(
    (s) => s.mode === 'takeover' && s.status === 'running',
  );
  const builtIds = useGenerationStore((s) => s.builtSectionIds);
  const buildActivity = useGenerationStore((s) => s.activity);
  // Follow the build: each landed brick scrolls the ghost (the next one) into view — the camera stays
  // on the work, like the video the scene was designed from.
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const builtCount = builtIds.length;
  useEffect(() => {
    if (!takeoverBuilding) return;
    const t = window.setTimeout(
      () => ghostRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }),
      350,
    );
    return () => window.clearTimeout(t);
  }, [takeoverBuilding, builtCount]);
  const visibleOrder = takeoverBuilding ? order.filter((id) => builtIds.includes(id)) : order;
  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const moveSection = useBuilderStore((s) => s.moveSection);
  const device = useBuilderStore((s) => s.device);
  const previewMode = useBuilderStore((s) => s.previewMode);
  // The store's live global theme drives the `--aqx-theme-*` override vars projected onto the edit
  // canvas (see `themeStyle` below). Subscribing here re-runs `applyThemeVars` on every theme edit so
  // the canvas re-themes live — the MemoSections don't re-render; they inherit the new CSS vars.
  const themeSettings = useBuilderStore((s) => s.themeSettings);
  const contentDir = useBuilderStore((s) => s.renderContext?.dir ?? 'ltr');
  // The SHOP's language, not the editor's: the app's countdown speaks to the buyer (its unit words come
  // from the storefront dictionary), so its preview must too.
  const canvasLocale = useBuilderStore((s) => s.renderContext?.locale ?? 'fr');

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // Resolve the document's product-picker refs into the render context (feature 0023, editor side) so a
  // section bound to a picked product paints THAT product here, exactly as the storefront will.
  usePickedProducts();

  // Resolve the header/footer's PICKED navigation menus the same way, so a picked menu paints its real
  // items here instead of an empty nav (the storefront gets them from the page bundle).
  useResolvedMenus();

  // Same for collections, both halves: the PICKED refs and the store's own ACTIVE collections. Without it
  // `collection-list` painted the built-in demo grid whatever the seller had actually configured.
  useResolvedCollections();

  // Feature 0041 V6 — the Countdown APP's campaigns for the page being edited. They are placed by their
  // own targeting rules and injected by the page reads, so nothing in the document could ever render them:
  // the canvas asks for them and paints what the storefront would. « Le builder doit la lire directement
  // de l'appli » (fondatrice, 28/09/2026).
  useCountdownPreview();

  // Edit-mode theme projection (feature 0019): the granular edit canvas renders `<MemoSection/>`
  // directly — NOT through `PageRenderer`, so it lacks PageRenderer's `<div data-aqx-page style=…>`
  // wrapper. Without it, `applyThemeVars(themeSettings)` never reaches the DOM and theme edits (accent,
  // fonts, sizes, scheme bands) DON'T show while editing — they'd only appear in Preview / on the live
  // store. We wrap the sections in the SAME `[data-aqx-page]` themed root the storefront uses so a theme
  // change re-skins every section live. (`[data-aqx-page]` also re-establishes the `aqx-page` query
  // container at the same width as the device frame, so responsive reflow is unchanged.)
  //
  // We project ONLY the `--aqx-theme-*` design tokens (which drive the full section cascade: base palette
  // → derived `--aqx-scheme-*` bands → typography → the calc()-rebased text sizes). `applyThemeVars` ALSO
  // emits legacy builder-chrome aliases (`--aqx-purple`/`--aqx-bg`/`--aqx-line`/…); we drop those here so
  // the in-canvas EditableSection selection outline + Fallback chrome keep the `.bld` Polaris palette
  // instead of being re-tinted to the seller's colors. No storefront section reads the legacy names.
  // Load the theme's heading/body WEBFONT into the builder document so the canvas renders the real family
  // (not a system fallback) — the storefront self-hosts these; the canvas otherwise only sets the family var.
  useEffect(() => {
    syncThemeFontLink(themeSettings);
  }, [themeSettings]);

  const themeStyle = useMemo<CSSProperties>(() => {
    const full = applyThemeVars(themeSettings) as Record<string, string>;
    const themeOnly: Record<string, string> = {};
    for (const [key, val] of Object.entries(full)) {
      if (key.startsWith('--aqx-theme-')) themeOnly[key] = val;
    }
    return themeOnly as CSSProperties;
  }, [themeSettings]);

  // Select-mode / drill-in AFFORDANCE only (the pointer). ALL hover + selection OUTLINES are drawn by the
  // measured <HoverFrame> / <SelectionFrame> overlays, NOT CSS `outline`: blocks are wrapped in a
  // `display:contents` span (0×0 box) so an outline on `[data-aqx-block-id]` paints nothing — the overlays
  // measure the real box instead (and hang a label on it). Never reaches the storefront.
  const selectModeCss =
    `.bld-canvas-secwrap [data-section-id]{cursor:pointer}` +
    `.bld-canvas-secwrap[data-select-mode] [data-aqx-block-id],` +
    `.bld-canvas-secwrap[data-selected] [data-aqx-block-id]{cursor:pointer}` +
    `.bld-canvas-secwrap[data-select-mode] [data-aqx-field-id],` +
    `.bld-canvas-secwrap[data-selected] [data-aqx-field-id]{cursor:pointer}`;

  // The canvas paints the REAL storefront sections, so every link in them is a REAL link: the header's
  // logo is `<a href="/">`, its cart/account icons point at `/cart` and `/account`, and the search
  // disclosure wraps a `<form action="/search">`. Clicking any of them navigated the builder SPA away and
  // it rebooted — which reads as "the section doesn't react". It bit the header hardest in the MOBILE
  // preview, where the 390px bar is almost entirely logo + icons and the centre (the natural place to
  // click) IS the logo link, so there was barely a pixel left that selected the section.
  //
  // We cancel only the browser's DEFAULT action, never the propagation: React's own handlers still run,
  // so `EditableSection`'s click-to-select and `MemoSection`'s drill-in are untouched. `<summary>` is
  // deliberately left alone — opening the drawer / search panel in the canvas is a useful preview.
  function onCanvasNavigateCapture(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest?.('a[href]')) e.preventDefault();
  }

  // The themed page root doubles as the positioning context for the <SelectionFrame> overlay: it lives
  // inside this scroll content, so its absolute offset stays glued to the target as the canvas scrolls.
  // Le nœud, pas seulement le `ref` : basculer l'aperçu ne démonte PAS ce composant, il rend un autre
  // arbre — le div ci-dessous part et revient en AUTRE nœud, et un `RefObject` étant stable, les effets
  // du filet d'images et du moteur d'animations ne se rebranchaient jamais. Voir `useHostNode`.
  const { attach: attachPage, node: pageHost, ref: pageRef } = useHostNode<HTMLDivElement>();
  const pageStyle = useMemo<CSSProperties>(
    () => ({ ...themeStyle, position: 'relative' }),
    [themeStyle],
  );

  // LES ANIMATIONS D'ENTRÉE, DANS L'ÉDITEUR (fondatrice, 18/09/2026). Le canvas n'armait rien et
  // n'observait rien : tout le groupe « Animations » était invisible pendant l'édition. La signature
  // porte ce que le moteur doit savoir — quelles sections existent, dans quel ordre, et le mouvement de
  // chacune — donc un ajout, un déplacement OU un réglage changé relance ce qu'il faut, et rien d'autre.
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const revealSignature = useMemo(
    () =>
      order
        .map((id) => {
          const st = (sectionsById[id]?.settings ?? {}) as Record<string, unknown>;
          const anim = typeof st.animation === 'string' ? st.animation : '';
          const pace = typeof st.animationPace === 'string' ? st.animationPace : '';
          return `${id}:${anim}:${pace}:${st.animationStagger === true ? '1' : '0'}`;
        })
        .join('\n'),
    [order, sectionsById],
  );
  useCanvasReveals({
    root: pageHost,
    signature: revealSignature,
    ids: order.join(','),
    selectedId: selectedSectionId,
  });
  useCanvasMediaRecovery(pageHost);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const labelFor = (id: UniqueIdentifier) => {
    const sec = sectionsById[String(id)];
    const raw = sec && sectionRegistry[sec.type]?.meta.label;
    return (
      (raw && sec ? bt(`secname.${sec.type}`, raw) : sec?.type) || bt('secname.fallback', 'Section')
    );
  };
  const positionOf = (id: UniqueIdentifier) => order.indexOf(String(id)) + 1;

  function onDragStart(e: DragStartEvent) {
    setActiveId(e.active.id);
  }
  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const to = order.indexOf(String(over.id));
    if (to !== -1) moveSection(String(active.id), to);
  }

  // Clean preview mode (build-plan §4.4): render the REAL PageRenderer with renderMode 'live' and NO
  // EditableSection chrome, so the seller sees exactly what a visitor sees (disabled sections hidden,
  // no outlines/handles, no dnd). It is the identical component tree — no iframe, no second engine.
  if (previewMode) {
    return (
      <DeviceFrame device={device}>
        {/* L'aperçu prétend montrer la page telle que l'acheteuse la voit : y cacher le compte à
            rebours de l'appli serait le pire des deux mondes (fondatrice, 29/09/2026 — « in preview
            mode the countdown doesn't show »). */}
        <CountdownAppChrome locale={canvasLocale} />
        <PreviewSurface />
      </DeviceFrame>
    );
  }

  if (order.length === 0) {
    return (
      <DeviceFrame device={device}>
        <div className="bld-canvas__empty" role="note">
          <p>{bt('chrome.canvas.empty', 'This page is empty.')}</p>
          <p>
            {bt(
              'chrome.canvas.empty_hint',
              'Add your first section from the rail to start building.',
            )}
          </p>
        </div>
      </DeviceFrame>
    );
  }

  return (
    <DeviceFrame device={device}>
      {/* The Countdown app's page-wide surfaces (0041 V6) — mounted by the FRAME, not by the page host,
          so the edit canvas and the clean preview paint them the same way. Outside the DnD context too:
          they are not sections and must never be sortable or droppable. */}
      <CountdownAppChrome locale={canvasLocale} />
      <div
        ref={attachPage}
        data-aqx-page=""
        dir={contentDir}
        style={pageStyle}
        className="bld-canvas-page"
        onClickCapture={onCanvasNavigateCapture}
        // Same reason as the links above: the header's search box is a real GET form to `/search`, so
        // pressing its button (or Enter in the field) reloaded the builder out of the page being edited.
        onSubmit={(e) => e.preventDefault()}
      >
        <style dangerouslySetInnerHTML={{ __html: selectModeCss }} />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
          accessibility={{
            announcements: buildAnnouncements(labelFor, positionOf, () => order.length, 'section'),
          }}
        >
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {/* A blue circular "+" sits on the TOP boundary (prepend) and on every boundary BETWEEN
                sections (build target 6) — appears on hover, opens the existing Add-section picker with the
                gap index, so it routes through the unchanged `insertSection(type, atIndex)` action. Hidden
                while a drag is in progress so it doesn't compete with the drop affordance. */}
            {!activeId && <InsertSectionAffordance atIndex={0} edge />}
            {visibleOrder.map((id, index) => (
              <Fragment key={id}>
                {takeoverBuilding ? (
                  <div className="bld-brick">
                    <MemoSection id={id} index={index} />
                  </div>
                ) : (
                  <MemoSection id={id} index={index} />
                )}
                {!activeId && !takeoverBuilding && (
                  <InsertSectionAffordance atIndex={index + 1} edge={index === order.length - 1} />
                )}
              </Fragment>
            ))}
            {takeoverBuilding && (
              // The NEXT brick, being made (the Lightfunnels placeholder, ours named): a section
              // WIREFRAME assembling in place — image block, heading, text lines, button — swept by a
              // sheen, with the label saying the work («Écrit les questions fréquentes…»), never a
              // spinner alone (0059 §5.2). Auto-scrolled into view as each brick lands.
              <div className="bld-ghost" role="status" aria-live="polite" ref={ghostRef}>
                <div className="bld-ghost__progress" aria-hidden="true" />
                <div className="bld-ghost__wire" aria-hidden="true">
                  <div className="bld-ghost__img">
                    <span className="bld-ghost__spark">✦</span>
                  </div>
                  <div className="bld-ghost__lines">
                    <i className="bld-ghost__h"></i>
                    <i></i>
                    <i></i>
                    <i className="bld-ghost__short"></i>
                    <span className="bld-ghost__btn"></span>
                  </div>
                </div>
                <span className="bld-ghost__label">
                  {buildActivity ?? bt('chrome.canvas.building', 'Building the rest…')}
                </span>
              </div>
            )}
          </SortableContext>
          <DragOverlay>
            {activeId ? <div className="bld-drag-ghost">{labelFor(activeId)}</div> : null}
          </DragOverlay>
        </DndContext>
        {/* Measured labelled frames — dashed for what the pointer is OVER (preview only: no panel opens
            until you click), solid for what IS selected. Both hidden while dragging so the ghost + drop
            affordance own the canvas. */}
        {!activeId && <HoverFrame pageRef={pageRef} />}
        {!activeId && <SelectionFrame pageRef={pageRef} />}
      </div>
    </DeviceFrame>
  );
}

/**
 * The clean-preview surface — the SAME `PageRenderer` the storefront serves, fed the live document
 * serialized from the store and a `renderMode: 'live'` context (so disabled sections drop out and no
 * editor chrome renders). Memoized on the document + context so it doesn't re-serialize every render.
 */
function PreviewSurface() {
  const order = useBuilderStore((s) => s.order);
  const sectionsById = useBuilderStore((s) => s.sectionsById);
  const meta = useBuilderStore((s) => s.meta);
  const context = useBuilderStore((s) => s.renderContext);
  // Same demo suppression as MemoSection: the walkthrough after a generation must never show the
  // demo catalog — an unbound section hides, exactly like on the live storefront.
  const suppressDemo = useGenerationStore(
    (s) => (s.mode === 'takeover' && s.status !== 'idle') || s.embeddedPreview,
  );

  const { attach: attachPreview, node: previewHost } = useHostNode<HTMLDivElement>();

  const previewReviews = useBuilderStore((s) => s.previewReviews);
  // The bound product's REAL reviews (feature 0066), merged in HERE rather than threaded through
  // `buildEditContext`: that context is rebuilt from a dozen places and a value passed through all of them
  // is a value that goes missing from one. Merged at render time, the canvas shows the shop's actual
  // numbers — it used to carry none at all, so `product-reviews` printed "0 reviews" on every store and a
  // seller who had just imported a file concluded the import had failed.
  // serializeComposed, NOT serialize: the canvas must render what the seller is EDITING — the page body
  // WITH the store's global chrome around it. `serialize` is the SAVE shape and strips the chrome out
  // into its own documents, so using it here would render every page without a header.
  const doc = useMemo(
    () => serializeComposed({ order, sectionsById, meta }),
    [order, sectionsById, meta],
  );
  // Does the page BODY open on a full-bleed visual? The EDIT canvas answers this in `MemoSection`, and
  // PREVIEW is a different render path — which is exactly how it got missed: the eye button showed an
  // opaque bar on a page the live store serves with a floating one (founder, 15/09/2026). Computed on the
  // body, chrome stripped, because the composed document starts with the header.
  const opensOnMedia = useMemo(
    () => pageOpensOnMedia(splitChromeSections(doc.sections).template),
    [doc],
  );
  // Cart mode, STORE-wide — the header's cart icon. Merged on this render path too, for the same reason as
  // `pageOpensOnMedia`: the eye button must show the icon the live store shows (see state/cartMode.ts).
  const cartMode = useStoreIsInCartMode();
  const liveContext = useMemo(
    () =>
      context
        ? {
            ...context,
            renderMode: 'live' as const,
            pageOpensOnMedia: opensOnMedia,
            buyBoxJourneys: canvasBuyBoxJourneys(cartMode),
            ...(previewReviews ? { reviews: previewReviews } : {}),
            ...(suppressDemo ? { catalogResolved: true } : {}),
          }
        : null,
    [context, suppressDemo, previewReviews, opensOnMedia, cartMode],
  );

  // Les mêmes entrées que sur la vitrine. Aucune sélection ici (l'aperçu n'a pas d'édition), donc pas
  // de garde-fou « la section sélectionnée est toujours visible » : le seul comportement est celui de
  // l'acheteuse.
  useCanvasReveals({
    root: previewHost,
    signature: doc.sections
      .map((sec) => {
        const st = (sec.settings ?? {}) as Record<string, unknown>;
        return `${sec.id}:${typeof st.animation === 'string' ? st.animation : ''}:${
          typeof st.animationPace === 'string' ? st.animationPace : ''
        }:${st.animationStagger === true ? '1' : '0'}`;
      })
      .join('\n'),
    ids: doc.sections.map((sec) => sec.id).join(','),
    selectedId: null,
  });
  useCanvasMediaRecovery(previewHost);

  if (!liveContext) return null;

  if (doc.sections.length === 0 || doc.sections.every((s) => s.disabled)) {
    return (
      <div className="bld-canvas__empty" role="note">
        <p>{bt('chrome.canvas.no_preview', 'Nothing to preview yet.')}</p>
        <p>{bt('chrome.canvas.no_preview_hint', 'Add a visible section to see your live page.')}</p>
      </div>
    );
  }

  // L'aperçu est censé être « exactement ce que voit un visiteur » — donc les entrées de section y
  // jouent aussi. Un div d'emballage tient la racine à observer : `PageRenderer` rend son propre
  // `[data-aqx-page]`, mais il ne nous donne pas de ref dessus. Le wrapper est `display:contents`,
  // il n'ajoute donc aucune boîte à la mise en page.
  return (
    <div ref={attachPreview} style={{ display: 'contents' }}>
      <PageRenderer document={doc} context={liveContext} />
    </div>
  );
}
